import visibilityChecker from './visibilityChecker';
import AudioController from '../class/AudioController'; // eslint-disable-line
import Gainable from '../mixin/Gainable';
import GroupedAudioContainer from '../_utils/audio/GroupedAudioContainer';
import {mix} from '../mixin';

const {document, setTimeout, clearTimeout, Promise, AudioBuffer} = globalThis;

let context = null;
let sampleRate = 44100;
let unlockStarted = false;
let unlocked = false;
let resolveUnlocked = null;
let scratchBuffer = null;
let autoSuspendable = true;
let suspendTimer = null;
let suspendingPromise = null;
let unlockQueueActive = true;

const AudioContext = globalThis.AudioContext || globalThis.webkitAudioContext;
const unlockedPromise = new Promise((resolve) =>
{
    resolveUnlocked = resolve;
});
const controllers = {};
const channels = {};
const buffers = {};
const playingAudios = new GroupedAudioContainer();
const pausedAudios = new GroupedAudioContainer();

const createContext = () =>
{
    if (!AudioContext) { return false; }
    if (!context)
    {
        context = new AudioContext({sampleRate});
        scratchBuffer = context.createBuffer(1, 1, 22050);
        Gainable.context = context;
        // Versuch, Context eigenständig zu resumen
        setTimeout(() => context.resume(), 10);
        context.addEventListener('statechange', stateChangeHandler);
        visibilityChecker.on('visibilityChange', (evt) =>
        {
            if (evt.subtype === 'focus' || !evt.visible) { return; }
            setTimeout(() => audioCore.resumeContext(), 250);
        });
    }
    return true;
};

const unlock = () =>
{
    if (!createContext() || unlocked) { return; }
    const source = context.createBufferSource();
    source.buffer = scratchBuffer;
    source.connect(context.destination);

    if (typeof source.start === 'undefined')
    {
        source.noteOn(0);
    }
    else
    {
        source.start(0);
    }

    context.resume();

    source.onended = () =>
    {
        source.disconnect();
        source.onended = null;
    };

    unlockStarted = true;
};

const stateChangeHandler = () =>
{
    if (context.state !== 'running' || unlocked) { return; }

    unlocked = true;
    audioCore.gainNode.connect(context.destination);
    resolveUnlocked();
    document.removeEventListener('touchstart', unlock, {once: true, capture: true});
    document.removeEventListener('touchend', unlock, {once: true, capture: true});
    document.removeEventListener('click', unlock, {once: true, capture: true});
    document.removeEventListener('keydown', unlock, {once: true, capture: true});
};

const startAutoSuspend = () =>
{
    if (playingAudios.count()) { return; }

    if (suspendTimer)
    {
        clearTimeout(suspendTimer);
        suspendTimer = null;
    }

    suspendTimer = setTimeout(() =>
    {
        suspendTimer = null;
        if (!autoSuspendable) { return; }

        suspendingPromise = context.suspend();
        suspendingPromise.finally(() =>
        {
            suspendingPromise = null;
        });
    }, 30000);
};

/**
 * @namespace audioCore
 * @see AudioController
 * @mixes Gainable
 * @borrows Gainable#muted as muted
 * @borrows Gainable#volume as volume
 * @borrows Gainable#gainNode as gainNode
 * @borrows Gainable#fadeTween as fadeTween
 * @borrows Gainable#fade as fade
 * @example
 * import {audioCore} from '@kids/lib/service';
 *
 * const controller = audioCore.createController('namespace1', './audios/namespace1');
 * controller.loadManifest([
 *     'swoosh1',
 *     {id: 'swoosh2', src: 'subfolder/swoosh2.mp3'},
 *     {id: 'swoosh3', sprites: {'swo': [0, 400], 'osh': [500, 400]}},
 *     {id: 'sfx', src: 'data:audio/wav;base64,...', sprites: {'peng': [0, 400], 'boom': [500, 400], 'wrooom': [1000, 400]}}
 * ]);
 *
 * controller.once('loaded', function()
 * {
 *     const peng = this.play('peng');
 *     peng.stop();
 * });
 */
const audioCore = mix(/** @lends audioCore */{
    /** Sollen vor dem Unlock gestartete Audios bei Unlock abgespielt werden?
     * @type {boolean}
     * @default true
     */
    set unlockQueueActive(active)
    {
        unlockQueueActive = active;
    },
    get unlockQueueActive()
    {
        return unlockQueueActive;
    },
    /** Setter: nach 30 Sekunden ohne Audioaktivität wird der Kontext suspendiert.
     * @type {boolean}
     * @default true
     * @see https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/suspend
     */
    set autoSuspendable(suspendable)
    {
        autoSuspendable = suspendable;
    },
    /**
     * @type {number}
     * @default 44100
     */
    set sampleRate(rate)
    {
        if (context)
        {
            console.warn('"sampleRate" could not be set because context already created.');
            return;
        }
        sampleRate = rate;
    },
    /**
     * @type {AudioContext}
     */
    get context()
    {
        createContext();
        return context;
    },
    /** Getter: Ein stiller Audio-Buffer.
     * @type {AudioBufferSourceNode}
     */
    get scratchBuffer()
    {
        createContext();
        return scratchBuffer;
    },
    /** Getter: Ein Promise, welches bei Unlock eingelöst wird.
     * @type {Promise}
     */
    get unlockedPromise()
    {
        return unlockedPromise;
    },
    /** Getter
     * @type {boolean}
     * @default false
     */
    get unlocked()
    {
        return unlocked;
    },
    get _unlockStarted()
    {
        return unlockStarted;
    },
    /**
     * @returns {Promise}
     */
    resumeContext()
    {
        if (suspendTimer)
        {
            clearTimeout(suspendTimer);
            suspendTimer = null;
        }

        if (suspendingPromise)
        {
            return suspendingPromise.finally(() => context.resume());
        }
        if (context.state === 'running')
        {
            return Promise.resolve();
        }

        return context.resume();
    },
    /**
     * @param {string=} namespace Ein Namespace, in dem Audios zusammengefasst werden und mit dem der Controller identifiziert
     * wird. Falls nicht übergeben, wird ein random String erzeugt.
     * @param {string} [basePath="./"] Der basePath, von dem die Audio-Dateien geladen werden sollen.
     * @returns {AudioController}
     */
    createController(namespace, basePath)
    {
        createContext();
        if (this.hasController(namespace))
        {
            return this.getController(namespace);
        }
        return new AudioController(namespace, basePath, true);
    },
    /**
     * @param {string} namespace
     * @returns {boolean}
     */
    hasController(namespace)
    {
        return controllers.hasOwnProperty(namespace);
    },
    /**
     * @param {string} namespace
     * @returns {AudioController|null}
     */
    getController(namespace)
    {
        return controllers[namespace] || null;
    },
    /**
     * @returns {Array}
     */
    getAllControllers()
    {
        return Object.values(controllers);
    },
    _setController(namespace, controller)
    {
        if (controller instanceof AudioController)
        {
            if (this.hasController(namespace))
            {
                throw new Error(
                    `Another audioController is already using the namespace "${namespace}".`
                );
            }

            controllers[namespace] = controller;
        }
        else
        {
            delete controllers[namespace];
        }
    },
    /** Setzt alle zuvor pausierten Audios (oder nur die eines Namespaces) wieder fort.
     * @param {string=} namespace
     */
    resumeAll(namespace)
    {
        pausedAudios.getAudios(namespace).forEach(audio => audio.play());
    },
    /** Pausiert alle spielenden Audios (oder nur die eines Namespaces).
     * @param {string=} namespace
     */
    pauseAll(namespace)
    {
        playingAudios.getAudios(namespace).forEach(audio => audio.pause());
    },
    /** Stoppt alle pausierten und spielenden Audios (oder nur die eines Namespaces).
     * @param {string=} namespace
     */
    stopAll(namespace)
    {
        [...playingAudios.getAudios(namespace), ...pausedAudios.getAudios(namespace)]
            .forEach(audio => audio.stop());
    },
    /** Entlädt alle pausierten und spielenden Audios (oder nur die eines Namespaces).
     * @param {string=} namespace
     */
    unloadAll(namespace)
    {
        [...playingAudios.getAudios(namespace), ...pausedAudios.getAudios(namespace)]
            .forEach(audio => audio.unload());
    },
    /** Beende das Audio im übergebenen Channel.
     * @param {string} name
     */
    clearChannel(name)
    {
        if (channels[name] !== undefined)
        {
            channels[name].stop();
            delete channels[name];
        }
    },
    /**
     * @param {string} name
     * @returns {boolean}
     */
    isChannelPlaying(name)
    {
        if (channels[name] !== undefined)
        {
            return channels[name].playing;
        }
        return false;
    },
    /** Überschreibe den Channel mit einem neuen Audio. In einem Channel kann immer nur ein Audio spielen.
     * @param {string} name
     * @param {Audio} audio
     */
    setChannel(name, audio)
    {
        if (channels[name] === audio) { return; }
        this.clearChannel(name);
        channels[name] = audio;
    },
    /** Beende alle Audios in Channels. */
    clearAllChannels()
    {
        Object.keys(channels).forEach(channel => this.clearChannel(channel));
    },
    /** Beende alle Audios in Channels, die einem Namespace zugeordnet sind.
     * @param {string} namespace
     */
    clearChannelsByNamespace(namespace)
    {
        const channelsToClear = [];
        for (const channel in channels)
        {
            if (channels[channel].namespace === namespace)
            {
                channelsToClear.push(channel);
            }
        }
        channelsToClear.forEach(channel => this.clearChannel(channel));
    },
    _setBuffer(key, buffer)
    {
        if (buffer instanceof AudioBuffer)
        {
            buffers[key] = buffer;
        }
        else
        {
            delete buffers[key];
        }
    },
    _getBuffer(key)
    {
        return buffers[key] || null;
    },
    _playAudio(audio, namespace = '__default')
    {
        playingAudios.addAudio(audio, namespace);
    },
    _pauseAudio(audio, namespace = '__default')
    {
        pausedAudios.addAudio(audio, namespace);
        playingAudios.removeAudio(audio, namespace);
        startAutoSuspend();
    },
    _stopAudio(audio, namespace = '__default')
    {
        playingAudios.removeAudio(audio, namespace);
        pausedAudios.removeAudio(audio, namespace);
        startAutoSuspend();
    }
}, true).with(Gainable);

document.addEventListener('touchstart', unlock, {once: true, capture: true});
document.addEventListener('touchend', unlock, {once: true, capture: true});
document.addEventListener('click', unlock, {once: true, capture: true});
document.addEventListener('keydown', unlock, {once: true, capture: true});

export default audioCore;
